import React, { useContext, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { DbEntryDateContext } from '../contexts/DbEntryDateContext';

// Helper to get first and last day of current month in YYYY-MM-DD format
const getMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0, 23, 59, 59); // Last second of the month
  const toISO = d => d.toISOString().slice(0, 10);
  return [toISO(firstDay), toISO(lastDay)];
};

// Helper to get first and last day of a given month/year
const getMonthRangeFromYM = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const toISO = d => d.toISOString().slice(0, 10);
  return [toISO(firstDay), toISO(lastDay)];
};

const SalesByMainOrderItem = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(getMonthRange()[0]);
  const [endDate, setEndDate] = useState(getMonthRange()[1]);
  const [granularity, setGranularity] = useState('month'); // default to month
  const [view, setView] = useState('table'); // 'table' or 'graph'
  const [top, setTop] = useState(10); // Top N entry, default 10
  const [controlsOpen, setControlsOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [showBottom, setShowBottom] = useState(false);
  const [showOptionsDetail, setShowOptionsDetail] = useState(false);
  const [optionsData, setOptionsData] = useState({ sizeBreakdown: [], optionBreakdown: [] });
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Menu selection state
  const [menus, setMenus] = useState([]);
  const [selectedMenus, setSelectedMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  const [showMenuPopup, setShowMenuPopup] = useState(false);
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const categoryPopupRef = useRef(null);
  const menuPopupRef = useRef(null);
  const locationPopupRef = useRef(null);
  const fetchTimeoutRef = useRef();
  const isResizingRef = useRef(false);
  const sidebarRef = useRef(null);

  // For month selection control
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const { dbEntryDate } = useContext(DbEntryDateContext);

  // Resizing handlers
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizingRef.current) {
        const newWidth = Math.max(180, Math.min(500, e.clientX - sidebarRef.current.getBoundingClientRect().left));
        setSidebarWidth(newWidth);
      }
    };
    const handleMouseUp = () => {
      isResizingRef.current = false;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Fetch menu list on mount
  useEffect(() => {
    axios.get('/api/reports/menus')
      .then(res => setMenus(res.data))
      .catch(() => setMenus([]));
  }, []);

  // Fetch categories when selectedMenus changes
  useEffect(() => {
    if (selectedMenus.length === 0) {
      setCategories([]);
      setSelectedCategories([]);
      return;
    }
    axios.get('/api/reports/categories-by-menu', {
      params: { menuIds: selectedMenus.join(',') }
    })
      .then(res => setCategories(res.data))
      .catch(() => setCategories([]));
  }, [selectedMenus]);

  // Fetch locations on mount
  useEffect(() => {
    axios.get('/api/reports/locations')
      .then(res => setLocations(res.data))
      .catch(() => setLocations([]));
  }, []);

  // Close popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (categoryPopupRef.current && !categoryPopupRef.current.contains(event.target)) {
        setShowCategoryPopup(false);
      }
    }
    if (showCategoryPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCategoryPopup]);

  // Close menu popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuPopupRef.current && !menuPopupRef.current.contains(event.target)) {
        setShowMenuPopup(false);
      }
    }
    if (showMenuPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenuPopup]);

  // Close location popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (locationPopupRef.current && !locationPopupRef.current.contains(event.target)) {
        setShowLocationPopup(false);
      }
    }
    if (showLocationPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLocationPopup]);

  // Sync selectedMonth when year changes
  useEffect(() => {
    const [, month] = selectedMonth.split('-');
    setSelectedMonth(`${selectedYear}-${month}`);
  }, [selectedYear]);

  // Update start/end date when month changes
  useEffect(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const [start, end] = getMonthRangeFromYM(year, month - 1);
    setStartDate(start);
    setEndDate(end);
    // eslint-disable-next-line
  }, [selectedMonth]);

  // Helper to move to previous or next month
  const changeMonth = (direction) => {
    const [yearStr, monthStr] = startDate.split('-');
    let year = parseInt(yearStr, 10);
    let month = parseInt(monthStr, 10) - 1; // JS months are 0-based

    if (direction === 'prev') {
      month -= 1;
      if (month < 0) {
        month = 11;
        year -= 1;
      }
    } else if (direction === 'next') {
      month += 1;
      if (month > 11) {
        month = 0;
        year += 1;
      }
    }
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const toISO = d => d.toISOString().slice(0, 10);
    setStartDate(toISO(firstDay));
    setEndDate(toISO(lastDay));
  };

  // Update fetchData to pass showBottom to API
  const fetchData = () => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    fetchTimeoutRef.current = setTimeout(() => {
      setLoading(true);
      setError('');
      setExpandedItems(new Set());
      const commonParams = {
        startDate,
        endDate,
        top,
        showBottom,
        selectedMenus: selectedMenus.join(','),
        selectedCategories: selectedCategories.join(','),
        selectedLocations: selectedLocations.join(','),
      };
      const mainFetch = axios.get('/api/reports/sales-by-main-order-items', {
        params: { ...commonParams, granularity },
      });
      const optionsFetch = showOptionsDetail
        ? axios.get('/api/reports/sales-by-main-order-items-options', { params: commonParams })
        : Promise.resolve({ data: { sizeBreakdown: [], optionBreakdown: [] } });

      Promise.all([mainFetch, optionsFetch])
        .then(([mainRes, optionsRes]) => {
          setData(mainRes.data);
          setOptionsData(optionsRes.data);
          setLoading(false);
        })
        .catch(() => {
          setError('Failed to fetch sales by main order items');
          setLoading(false);
        });
    }, 125);
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    //fetchData();
    // eslint-disable-next-line
  }, [startDate, endDate, granularity, top, selectedMenus, selectedCategories, selectedLocations, showBottom, showOptionsDetail]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  // CSV download functionality
  const downloadCSV = () => {
    if (!data.length) return;
    const headers = ['Main Order Item', 'Menu Item ID', 'Period', 'Period End Date', 'Total Sales'];
    const rows = data.map(row => [
      row.itemName,
      row.menuItemId,
      row.period,
      row.periodEndDate,
      row.totalSales
    ]);
    const csvContent =
      [headers, ...rows]
        .map(e => e.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
        .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sales_by_main_order_item.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Format date for chart labels, e.g. "Jun 3"
  const formatChartDate = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  };

  // Helper to format pennies to dollars with comma, no dollar sign
  const formatDollars = (pennies) => {
    const dollars = Math.round(Number(pennies)) / 100;
    return dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Prepare data for the line chart
  const getChartData = () => {
    const periodEndDates = Array.from(new Set(data.map(row => row.periodEndDate))).sort();
    const mainOrderItems = Array.from(new Set(data.map(row => row.itemName)));
    const datasets = mainOrderItems.map((mainOrderItem, idx) => ({
      label: mainOrderItem,
      data: periodEndDates.map(periodEndDate => {
        const found = data.find(row =>
          row.itemName === mainOrderItem &&
          row.periodEndDate === periodEndDate
        );
        return found ? Number(found.totalSales) : 0;
      }),
      fill: false,
      borderColor: `hsl(${(idx * 60) % 360}, 70%, 50%)`,
      backgroundColor: `hsl(${(idx * 60) % 360}, 70%, 50%)`,
      tension: 0.2,
    }));

    return {
      labels: periodEndDates.map(formatChartDate),
      datasets,
    };
  };

  // Handle menu selection (multi-select)
  const handleMenuChange = (e) => {
    const options = Array.from(e.target.selectedOptions);
    setSelectedMenus(options.map(opt => opt.value));
  };

  // Handle category selection (multi-select)
  const handleCategoryChange = (e) => {
    const options = Array.from(e.target.selectedOptions);
    setSelectedCategories(options.map(opt => opt.value));
  };

  // Handle location selection (multi-select)
  const handleLocationChange = (e) => {
    const options = Array.from(e.target.selectedOptions);
    setSelectedLocations(options.map(opt => opt.value));
  };

  // Handle category checkbox change
  const handleCategoryCheckbox = (catId) => {
    setSelectedCategories((prev) =>
      prev.includes(catId)
        ? prev.filter((id) => id !== catId)
        : [...prev, catId]
    );
  };

  // Handle menu checkbox change
  const handleMenuCheckbox = (menuId) => {
    setSelectedMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  // Handle location checkbox change
  const handleLocationCheckbox = (locId) => {
    setSelectedLocations((prev) =>
      prev.includes(locId)
        ? prev.filter((id) => id !== locId)
        : [...prev, locId]
    );
  };

  // Helper to get display names for selected menus, categories, and locations
  const selectedMenuNames = menus
    .filter(menu => selectedMenus.includes(menu.item_id))
    .map(menu => menu.name)
    .join(', ') || 'All Menus';
  const selectedCategoryNames = categories
    .filter(cat => selectedCategories.includes(cat.categoryId))
    .map(cat => cat.categoryName)
    .join(', ') || 'All Categories';

  const selectedLocationNames = locations
    .filter(loc => selectedLocations.includes(loc.ID))
    .map(loc => loc.NAME || `${loc.CITY} ${loc.NAME}`)
    .join(', ') || 'All Locations';

  const toggleExpand = (rowKey) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  // Build the size/option breakdown for a given menu item, grouped by size.
  // The breakdown is aggregated across the whole selected date range.
  const getItemDetail = (menuItemId) => {
    const id = String(menuItemId);
    const sizes = optionsData.sizeBreakdown.filter(s => String(s.menuItemId) === id);
    const options = optionsData.optionBreakdown.filter(o => String(o.menuItemId) === id);
    if (sizes.length === 0 && options.length === 0) return null;

    const sizeMap = new Map();
    const keyFor = name => name || '';
    sizes.forEach(s => {
      sizeMap.set(keyFor(s.sizeName), { sizeName: s.sizeName, sizeCount: s.sizeCount, options: [] });
    });
    options.forEach(o => {
      const k = keyFor(o.sizeName);
      if (!sizeMap.has(k)) sizeMap.set(k, { sizeName: o.sizeName, sizeCount: null, options: [] });
      sizeMap.get(k).options.push(o);
    });
    return Array.from(sizeMap.values());
  };

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;
    const sorted = [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      if (sortConfig.key === 'totalRevenue' || sortConfig.key === 'totalSales') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [data, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      // For totalSales and totalRevenue, first click is descending
      if (key === 'totalSales' || key === 'totalRevenue') {
        if (prev.key !== key) {
          return { key, direction: 'desc' };
        }
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      // For other columns, first click is ascending
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  return (
    <div style={{ fontFamily: 'Inter, Roboto, Arial, sans-serif' }}>
      <h2>
        Sales by Main Order Item
        {dbEntryDate && (
          <span style={{ fontWeight: 400, fontSize: '0.85rem', marginLeft: 8, color: '#666' }}>
            as of {dbEntryDate}
          </span>
        )}
      </h2>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2rem' }}>
        {/* Collapsible controls sidebar with resizing bar and twisty */}
        <div
          ref={sidebarRef}
          style={{
            minWidth: controlsOpen ? sidebarWidth : 5,
            maxWidth: controlsOpen ? sidebarWidth : 5,
            width: controlsOpen ? sidebarWidth : 5,
            background: '#f7f9fc',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(25,118,210,0.04)',
            padding: controlsOpen ? '1.5rem 1rem' : '0.5rem',
            marginBottom: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: controlsOpen ? '1.1rem' : '0.5rem',
            transition: 'all 0.2s',
            position: 'relative',
            overflow: 'visible',
            fontFamily: 'inherit',
          }}
        >
          {/* Twist to hide/show controls */}
          <div
            style={{
              position: 'absolute',
              top: 16,
              right: controlsOpen ? sidebarWidth - 24 : 5,
              zIndex: 2100,
              cursor: 'pointer',
              width: 24,
              height: 24,
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: '50%',
              boxShadow: '0 2px 8px rgba(25,118,210,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'right 0.2s',
              fontFamily: 'inherit',
            }}
            onClick={() => setControlsOpen((open) => !open)}
            title={controlsOpen ? 'Hide Controls' : 'Show Controls'}
          >
            <span
              style={{
                display: 'inline-block',
                transition: 'transform 0.2s',
                fontSize: '1.2rem',
                color: '#1976d2',
                transform: controlsOpen ? 'rotate(90deg)' : 'rotate(-90deg)',
                userSelect: 'none',
                fontFamily: 'inherit',
              }}
            >
              ▶
            </span>
          </div>
          {/* Resizing bar */}
          {controlsOpen && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: -6,
                width: 12,
                height: '100%',
                cursor: 'ew-resize',
                zIndex: 2000,
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'inherit',
              }}
              onMouseDown={() => { isResizingRef.current = true; }}
              title="Resize sidebar"
            >
              <div
                style={{
                  width: 4,
                  height: '60px',
                  background: '#ccc',
                  borderRadius: 2,
                  margin: 'auto',
                  opacity: 0.7,
                  fontFamily: 'inherit',
                }}
              />
            </div>
          )}
          {/* Controls form */}
          {controlsOpen && (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', fontFamily: 'inherit' }}>
              {/* Month selection control with year selector */}
              <label>
                Month:&nbsp;
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    type="button"
                    style={{
                      padding: '2px 8px',
                      fontSize: '0.95rem',
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      background: '#fff',
                      cursor: 'pointer'
                    }}
                    onClick={() => setSelectedYear(y => y - 1)}
                  >
                    &#10913;
                  </button>
                  <button
                    type="button"
                    style={{
                      padding: '2px 8px',
                      fontSize: '0.95rem',
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      background: '#fff',
                      cursor: 'pointer'
                    }}
                    onClick={() => changeMonth('prev')}
                  >
                    &lt;
                  </button>                  
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={e => setSelectedMonth(e.target.value)}
                    style={{ fontSize: '0.75rem', padding: '2px 6px' , width: '90px' }}
                  />
                  <button
                    type="button"
                    style={{
                      padding: '2px 8px',
                      fontSize: '0.95rem',
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      background: '#fff',
                      cursor: 'pointer'
                    }}
                    onClick={() => changeMonth('next')}
                  >
                    &gt;
                  </button>
                  <button
                    type="button"
                    style={{
                      padding: '2px 8px',
                      fontSize: '0.95rem',
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      background: '#fff',
                      cursor: 'pointer'
                    }}
                    onClick={() => setSelectedYear(y => y + 1)}
                  >
                    &#10914;
                  </button>
                </div>
              </label>
              <label>
                Start Date:&nbsp;
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
              </label>
              <label>
                End Date:&nbsp;
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                />
              </label>
              <label>
                Granularity:&nbsp;
                <select
                  value={granularity}
                  onChange={e => setGranularity(e.target.value)}
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </select>
              </label>
              <label>
                <button
                  type="button"
                  style={{
                    padding: '2px 10px',
                    fontSize: '0.95rem',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    background: showBottom ? '#388e3c' : '#1976d2',
                    color: '#fff',
                    cursor: 'pointer',
                    marginRight: '0.5rem'
                  }}
                  onClick={() => setShowBottom((v) => !v)}
                >
                  {showBottom ? 'Bottom' : 'Top'}
                </button>
                <input
                  type="number"
                  min={1}
                  value={top}
                  onChange={e => setTop(Number(e.target.value))}
                  style={{ width: 60 }}
                />
                &nbsp;{showBottom ? 'Lowest Sold' : 'Highest Sold'}
              </label>
              <label>
                Menus:&nbsp;
                <button
                  type="button"
                  onClick={() => setShowMenuPopup((v) => !v)}
                  style={{ padding: '2px 10px', cursor: 'pointer' }}
                >
                  {selectedMenus.length === 0
                    ? 'Select Menus'
                    : `${selectedMenus.length} selected`}
                </button>
              </label>
              {showMenuPopup && (
                <div
                  ref={menuPopupRef}
                  style={{
                    position: 'absolute',
                    left: controlsOpen ? '100%' : '48px',
                    top: 60,
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    padding: 12,
                    zIndex: 1000,
                    maxHeight: 250,
                    width: '270px', // 50% wider than previous 180px default
                    overflowY: 'auto',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    fontSize: '0.82rem',
                    fontFamily: 'inherit',
                  }}
                >
                  {menus.length === 0 && <div style={{ color: '#888' }}>No menus</div>}
                  {menus.map((menu, idx) => (
                    <div key={`${menu.item_id}-${idx}`}>
                      <label style={{ fontSize: '0.82rem', fontFamily: 'inherit' }}>
                        <input
                          type="checkbox"
                          checked={selectedMenus.includes(menu.item_id)}
                          onChange={() =>
                            handleMenuCheckbox(menu.item_id)
                          }
                        />
                        &nbsp;{menu.name}
                      </label>
                    </div>
                  ))}
                  <div style={{ marginTop: 8 }}>
                    <button type="button" onClick={() => setShowMenuPopup(false)}>
                      Done
                    </button>
                  </div>
                </div>
              )}
              <label>
                Categories:&nbsp;
                <button
                  type="button"
                  onClick={() => setShowCategoryPopup((v) => !v)}
                  style={{ padding: '2px 10px', cursor: 'pointer' }}
                >
                  {selectedCategories.length === 0
                    ? 'Select Categories'
                    : `${selectedCategories.length} selected`}
                </button>
              </label>
              {showCategoryPopup && (
                <div
                  ref={categoryPopupRef}
                  style={{
                    position: 'absolute',
                    left: controlsOpen ? '100%' : '48px',
                    top: 180,
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    padding: 12,
                    zIndex: 1000,
                    maxHeight: 250,
                    width: '270px', // 50% wider
                    overflowY: 'auto',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    fontSize: '0.82rem',
                    fontFamily: 'inherit',
                  }}
                >
                  {categories.length === 0 && <div style={{ color: '#888' }}>No categories</div>}
                  {categories.map((cat, idx) => (
                    <div key={`${cat.categoryId}-${idx}`}>
                      <label style={{ fontSize: '0.82rem', fontFamily: 'inherit' }}>
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(
                            cat.categoryId
                          )}
                          onChange={() =>
                            handleCategoryCheckbox(cat.categoryId)
                          }
                        />
                        &nbsp;{cat.categoryName}
                      </label>
                    </div>
                  ))}
                  <div style={{ marginTop: 8 }}>
                    <button type="button" onClick={() => setShowCategoryPopup(false)}>
                      Done
                    </button>
                  </div>
                </div>
              )}
              <label>
                Locations:&nbsp;
                <button
                  type="button"
                  onClick={() => setShowLocationPopup((v) => !v)}
                  style={{ padding: '2px 10px', cursor: 'pointer' }}
                >
                  {selectedLocations.length === 0
                    ? 'Select Locations'
                    : `${selectedLocations.length} selected`}
                </button>
              </label>
              {showLocationPopup && (
                <div
                  ref={locationPopupRef}
                  style={{
                    position: 'absolute',
                    left: controlsOpen ? '100%' : '48px',
                    top: 300,
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    padding: 12,
                    zIndex: 1000,
                    maxHeight: 250,
                    width: '270px', // 50% wider
                    overflowY: 'auto',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    fontSize: '0.82rem',
                    fontFamily: 'inherit',
                  }}
                >
                  {locations.length === 0 && <div style={{ color: '#888' }}>No locations</div>}
                  {locations.map((loc, idx) => (
                    <div key={`${loc.ID}-${idx}`}>
                      <label style={{ fontSize: '0.82rem', fontFamily: 'inherit' }}>
                        <input
                          type="checkbox"
                          checked={selectedLocations.includes(loc.ID)}
                          onChange={() =>
                            handleLocationCheckbox(loc.ID)
                          }
                        />
                        &nbsp;{loc.NAME} {loc.CITY ? `(${loc.CITY})` : ''}
                      </label>
                    </div>
                  ))}
                  <div style={{ marginTop: 8 }}>
                    <button type="button" onClick={() => setShowLocationPopup(false)}>
                      Done
                    </button>
                  </div>
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input
                  type="checkbox"
                  checked={showOptionsDetail}
                  onChange={e => setShowOptionsDetail(e.target.checked)}
                />
                Show Options Detail
              </label>
              <button type="submit">Apply</button>
              <button type="button" onClick={downloadCSV} disabled={!data.length}>
                Download CSV
              </button>
              <button
                type="button"
                onClick={() => setView(view === 'table' ? 'graph' : 'table')}
                disabled={loading || !!error}
              >
                {view === 'table' ? 'Show Graph' : 'Show Table'}
              </button>
            </form>
          )}
        </div>
        {/* Main content area */}
        <div style={{ flex: 1, fontFamily: 'inherit' }}>
          <div style={{ marginBottom: 10, fontWeight: 500 }}>
            Locations: <span style={{ color: '#8e24aa' }}>{selectedLocationNames}</span>
            &nbsp;|&nbsp;
            Menus: <span style={{ color: '#1976d2' }}>{selectedMenuNames}</span>
            &nbsp;|&nbsp;
            Categories: <span style={{ color: '#388e3c' }}>{selectedCategoryNames}</span>
          </div>
          {!loading && <p>Press Apply to Run Report</p>} {loading && <p>Loading...</p>}
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {!loading && !error && view === 'table' && (
            <table border="1" cellPadding="8" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th>Main Order Item</th>
                  <th>Menu Item ID</th>
                  <th>
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }} onClick={() => handleSort('period')}>
                      Period {sortConfig.key === 'period' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                    </button>
                  </th>
                  <th>Period End Date</th>
                  <th>
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }} onClick={() => handleSort('totalSales')}>
                      Total Sold {sortConfig.key === 'totalSales' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                    </button>
                  </th>
                  <th>
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }} onClick={() => handleSort('totalRevenue')}>
                      Total Revenue {sortConfig.key === 'totalRevenue' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, idx) => {
                  const detail = showOptionsDetail ? getItemDetail(row.menuItemId) : null;
                  const isExpanded = expandedItems.has(idx);
                  return (
                    <React.Fragment key={idx}>
                      <tr>
                        <td>
                          {detail && (
                            <button
                              type="button"
                              onClick={() => toggleExpand(idx)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#1976d2',
                                fontSize: '0.9rem',
                                marginRight: 6,
                                padding: 0,
                              }}
                              title={isExpanded ? 'Hide options detail' : 'Show options detail'}
                            >
                              {isExpanded ? '▼' : '▶'}
                            </button>
                          )}
                          {row.itemName}
                        </td>
                        <td>{row.menuItemId}</td>
                        <td>{row.period}</td>
                        <td>{row.periodEndDate}</td>
                        <td>{row.totalSales}</td>
                        <td>{formatDollars(row.totalRevenue)}</td>
                      </tr>
                      {detail && isExpanded && detail.map((size, sIdx) => (
                        <React.Fragment key={`${idx}-size-${sIdx}`}>
                          <tr style={{ background: '#f4f8ff' }}>
                            <td colSpan={4} style={{ paddingLeft: 36, fontWeight: 600 }}>
                              {size.sizeName || '(no size)'}
                            </td>
                            <td>{size.sizeCount != null ? size.sizeCount : ''}</td>
                            <td></td>
                          </tr>
                          {size.options.map((opt, oIdx) => (
                            <tr key={`${idx}-size-${sIdx}-opt-${oIdx}`} style={{ background: '#fafbfc' }}>
                              <td colSpan={4} style={{ paddingLeft: 56, color: '#555' }}>
                                {opt.optionName}
                              </td>
                              <td>{opt.optionCount}</td>
                              <td>{formatDollars(opt.optionRevenue)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
          {!loading && !error && view === 'graph' && (
            <div style={{ background: '#fff', padding: '1rem', borderRadius: 8 }}>
                        <div
            style={{
              background: '#fff',
              padding: '1rem',
              borderRadius: 8,
              width: '100%',
              height: '100%',
              minHeight: '300px',
              minHeight: 600,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
              <Line
                data={getChartData()}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'top' },
                    title: { display: true, text: 'Sales by Main Order Item Over Time' },
                  },
                  scales: {
                    y: { beginAtZero: true, title: { display: true, text: 'Total Sales' } },
                    x: { title: { display: true, text: 'Period End Date' } },
                  },
                }}
                /*height={400}*/
                style={{ width: '100%', height: '100%' }}
              />
             </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesByMainOrderItem;